import { ILayer } from '../layer.type';
import { _ } from '~shared/utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { environment } from '~environments/environment';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';
import { merge } from 'lodash-es';

const markerUrlBase = environment.assetRoot + '/data/T13/markers/';

/**
 * Keski-Savon Vesi
 */
export interface KeskiSavonTenantConfig extends TenantConfig {

}

export default (): KeskiSavonTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 62.323,
    lng: 27.866,
  },
  defaultZoom: 11.40,
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-single',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      }
    ]
  }],
  vivo: {
    diaryTaskType: '60d3020ee7f7c50485f598b2',
    lookupPropertyId: '61559d61da6bc6731fa1a7f2',
  },
  useGlobalFlags: true,
  renderFlagFilteringForLayers: true
});

export function pointLayer(overrides: Partial<ILayer> & { id: string, name: string }) : ILayer {
  return merge({
    belowRoadLayers: false,
    selectable: true,
    linkable: true,
    style: {
      layerType: 'symbol',
      sourceType: 'geojson',
      clickable: true,
      visible: true,
    },
    mapboxOptions: {
      layout: {
        'text-allow-overlap': true,
        'text-anchor': 'top',
        'text-radial-offset': 1,
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          0, 3,
          13, 12,
          16, 15,
        ],
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          0, 0.2,
          10, 0.4,
          16, 1.0,
        ],
        'text-field': ['get', 'Positionumero'],
      },
      paint: {
        'text-color': 'black',
        'text-halo-color': 'white',
        'text-halo-width': 2,
        'icon-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
        'text-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
      }
    },
    metadata: {
      uniqueBy: 'Positiotunnus',
      labelBy: 'Laitteen nimi',
      group: _('Facilities'),
      component: 'ElementDetailsComponent',
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        allProperties: true,
        hideProperties: ['Rego symboli', 'Positionumero']
      },
      measurementsFilters: {
        tunnus: 'Positiotunnus',
      },
    },
    searchOptions: {
      searchable: true,
      interestingProperties: [{
        key: 'Positiotunnus',
        type: 'startsWith',
      }, {
        key: 'Laitteen nimi',
        type: 'contains',
        priority: 1,
      }]
    },
    useWorker: true,
  }, overrides);
}

export function backroundPointLayer(overrides: Partial<ILayer> & { id: string, name: string }) : ILayer {
  return merge({
    belowRoadLayers: false,
    orderingWeight: 20,
    selectable: false,
    linkable: false,
    metadata: {
      uniqueBy: 'fid',
      labelBy: 'fid',
      group: _('Networks'),
      component: 'ElementDetailsComponent',
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        allProperties: false,
        hideProperties: []
      },
      visibleByZoom: 10,
      measurementsFilters: {
        tunnus: 'fid',
      },
    },
    searchOptions: {
      searchable: false,
      interestingProperties: [{
        key: 'fid',
        type: 'startsWith',
      }]
    },
    style: {
      visible: true,
      layerType: 'circle',
      sourceType: 'vector'
    },
    mapboxOptions: {
      paint: {
        "circle-stroke-color": "black",
        "circle-stroke-width": 2,
        "circle-stroke-opacity": 0.75,
      },
    },
    useWorker: false,
  }, overrides);
}

export function lineLayer(overrides: Partial<ILayer> & { id: string, name: string }) : ILayer {
  return merge({
    selectable: true,
    style: {
      clickable: true,
      strokeWeight: 1.5,
      visible: true,
      layerType: 'line',
      sourceType: 'vector',
    },
    metadata: {
      visibleByZoom: 10,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
      uniqueBy: 'EntityHandle',
      labelBy: 'EntityHandle',
      group: _('Networks'),
    },
    searchOptions: {
      interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
      searchable: false,
    },
    useWorker: true,
  }, overrides);
}

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T15.json by MapService.addLayer
export function getLayers(): ILayer[] {
  return [
    // point layers
    pointLayer({
      id: 'JVP_jvpumppaamo',
      name: _('Wastewater pumping stations'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
      },
    }),
    pointLayer({
      id: 'JVL_jvpuhdistamo',
      name: _('Wastewater plants'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'jätevesilaitos4.png',
        },
      },
    }),
    pointLayer({
      id: 'VL_vesilaitos',
      name: _('Waterworks'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'Vesilaitos2.png',
        },
      },
    }),
    pointLayer({
      id: 'VO_vedenottamo',
      name: _('Water intakes'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'Vedenottamo.png',
        },
      },
    }),
    pointLayer({
      id: 'VT_vesitorni',
      name: _('Water towers'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'Vesitorni.png',
        },
      },
    }),
    pointLayer({
      id: 'PK_paineenkorottamo',
      name: _('Booster stations'),
      orderingWeight: 1,
      style: {
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
      }
    }),
    backroundPointLayer({
      id: 'Runkoventtiilit',
      name: _('Main valves'),
      mapboxOptions: {
        paint: {
          "circle-radius": 7,
          "circle-color": "LightBlue",
          "circle-opacity": 0.25,
        },
      }
    }),
    backroundPointLayer({
      id: 'Tonttiventtiilit',
      name: _('Household valves'),
      mapboxOptions: {
        paint: {
          "circle-radius": 4,
          "circle-color": "DeepSkyBlue",
          "circle-opacity": 0.65,
        },
      }
    }),
    // line layers
    lineLayer({
      id: 'SVV',
      name: _('Runoff'),
      vectorSourceLayer: 'SVV',
      orderingWeight: 10,
      style: {
        strokeColor: '#006400',
      },
    }),
    lineLayer({
      id: 'SVPAINE',
      name: _('Pressurized runoff'),
      vectorSourceLayer: 'SVPAINE',
      orderingWeight: 10,
      style: {
        strokeColor: '#006400',
        strokeWeight: 3.0,
      },
    }),
    lineLayer({
      id: 'JVV',
      name: _('Wastewater'),
      vectorSourceLayer: 'JVV',
      orderingWeight: 10,
      style: {
        strokeColor: '#fd0150',
      },
    }),
    lineLayer({
      id: 'JVPAINE',
      name: _('Pressurized wastewater'),
      vectorSourceLayer: 'JVPAINE',
      orderingWeight: 10,
      style: {
        strokeColor: '#fd0150',
        strokeWeight: 3.0,
      },
    }),
    lineLayer({
      id: 'VJ',
      name: _('Tap water'),
      vectorSourceLayer: 'VJ',
      orderingWeight: 10,
      style: {
        strokeColor: '#000080',
      },
    }),

    // shared layers
    getSharedLayer('houseNumbers'),
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    getSharedLayer('groundWaterAreas'),
    getSharedLayer('drainageAreas'),
    getSharedLayer('basinOutlets'),
  ];
}
